// ═══════════════════════════════════════════════════════════════
// recommendations — work the tech suggests but the customer decides
// on, line by line.
//
// Kept SEPARATE from the job's lineItems, which are the work already
// agreed to (the inspection fee, whatever was booked). A
// recommendation is à-la-carte: the customer accepts or declines each
// one, and the estimate total moves as they do.
//
//   lineItems      → what they're already paying for
//   recommendations → what you're suggesting on top of it
//
// Each recommendation keeps the inspection finding it came from,
// including that finding's photos, so the customer reads "your belt
// is cracked" with the photo of the crack next to the price.
//
// Declined ones are never deleted. They stay on the job as a record
// of what was offered, which is what lets the next visit open with
// "last time you passed on the rear brakes."
// ═══════════════════════════════════════════════════════════════
import { shortId, toDate } from './job-helpers.js';

export const REC_PENDING  = 'pending';
export const REC_ACCEPTED = 'accepted';
export const REC_DECLINED = 'declined';

export function blankRecommendation() {
  return {
    id: shortId(),
    label: '',
    detail: '',
    price: 0,
    urgency: 'attention',   // mirrors the inspection scale
    sourceKey: null,        // the finding this came from, if any
    photoUrls: [],
    status: REC_PENDING,
  };
}

// Price parsing has the same trap as everywhere else in this app:
// Number('') and Number(null) are both 0 and both finite. Here 0 is a
// legitimate price (a free courtesy item), so an empty field and a
// deliberate zero must stay distinguishable at the input layer even
// though both end up costing nothing.
export function toPrice(value) {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'string' && value.trim() === '') return 0;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export function normalizeRecommendation(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const label = (raw.label || '').trim();
  if (!label) return null;
  const status = [REC_ACCEPTED, REC_DECLINED, REC_PENDING].includes(raw.status)
    ? raw.status
    : REC_PENDING;
  return {
    id: raw.id || shortId(),
    label,
    detail: (raw.detail || '').trim(),
    price: toPrice(raw.price),
    urgency: raw.urgency === 'replace' ? 'replace' : 'attention',
    sourceKey: raw.sourceKey || null,
    photoUrls: Array.isArray(raw.photoUrls) ? raw.photoUrls.filter(Boolean) : [],
    status,
  };
}

// Drops unlabelled rows — a half-typed recommendation isn't an offer.
export function normalizeRecommendations(list) {
  if (!Array.isArray(list)) return [];
  return list.map(normalizeRecommendation).filter(Boolean);
}

// Seed a recommendation from something the inspection flagged, so the
// tech only has to type the price.
export function recommendationFromFinding(finding) {
  return {
    ...blankRecommendation(),
    label: finding.label || '',
    detail: (finding.note || '').trim(),
    urgency: finding.status === 'replace' ? 'replace' : 'attention',
    sourceKey: finding.key || null,
    photoUrls: (finding.photos || []).map((p) => p?.url).filter(Boolean),
  };
}

// ─── Totals ─────────────────────────────────────────────────────
export function recommendationsTotal(recs) {
  return normalizeRecommendations(recs).reduce((s, r) => s + r.price, 0);
}

export function acceptedTotal(recs) {
  return normalizeRecommendations(recs)
    .filter((r) => r.status === REC_ACCEPTED)
    .reduce((s, r) => s + r.price, 0);
}

export function splitByStatus(recs) {
  const all = normalizeRecommendations(recs);
  return {
    pending:  all.filter((r) => r.status === REC_PENDING),
    accepted: all.filter((r) => r.status === REC_ACCEPTED),
    declined: all.filter((r) => r.status === REC_DECLINED),
  };
}

// What the customer owes: the work already agreed plus whatever they
// said yes to. Before they decide anything, nothing extra is counted —
// an undecided recommendation is not a charge.
export function grandTotal(baseTotal, recs) {
  return toPrice(baseTotal) + acceptedTotal(recs);
}

// ─── Customer decisions ─────────────────────────────────────────
// The public page writes a flat { [recId]: 'accepted' | 'declined' }
// map. Fold it back onto the recommendations, ignoring ids we don't
// recognise and any value that isn't one of the two verbs.
export function applyDecisions(recs, decisions) {
  const map = decisions && typeof decisions === 'object' ? decisions : {};
  return normalizeRecommendations(recs).map((r) => {
    const d = map[r.id];
    if (d !== REC_ACCEPTED && d !== REC_DECLINED) return r;
    return { ...r, status: d };
  });
}

export function allDecided(recs) {
  const all = normalizeRecommendations(recs);
  return all.length > 0 && all.every((r) => r.status !== REC_PENDING);
}

// When a job is cancelled, anything the customer never answered
// becomes a decline — they didn't say yes, and the job is dead.
//
// Recommendations they ALREADY accepted stay accepted. The job may
// have been cancelled for a reason that has nothing to do with their
// answer (vehicle sold, scheduling), and rewriting a yes into a no
// would put words in their mouth.
export function declinePendingRecommendations(recs) {
  return normalizeRecommendations(recs).map((r) =>
    r.status === REC_PENDING ? { ...r, status: REC_DECLINED } : r
  );
}

// ─── Declined-work history ──────────────────────────────────────
// Everything this customer has ever passed on, newest first, so the
// next visit can open with what they said no to last time.
//
// Skips anything they've since accepted on a later job: matching by
// label is crude, but re-offering a repair the customer already paid
// for is the one mistake that makes this feature embarrassing rather
// than useful.
export function declinedHistory(jobs, { vehicleId = null } = {}) {
  const list = Array.isArray(jobs) ? jobs : [];

  const acceptedLabels = new Set();
  list.forEach((j) => {
    normalizeRecommendations(j.recommendations)
      .filter((r) => r.status === REC_ACCEPTED)
      .forEach((r) => acceptedLabels.add(r.label.toLowerCase()));
  });

  const out = [];
  list.forEach((job) => {
    if (vehicleId && job.vehicleId !== vehicleId) return;
    normalizeRecommendations(job.recommendations)
      .filter((r) => r.status === REC_DECLINED)
      .forEach((r) => {
        if (acceptedLabels.has(r.label.toLowerCase())) return;
        out.push({
          ...r,
          jobId: job.id,
          jobNumber: job.jobNumber ?? null,
          vehicleId: job.vehicleId || null,
          declinedAt: toDate(job.decidedAt) || toDate(job.approvedAt) || toDate(job.createdAt),
        });
      });
  });

  return out.sort((a, b) => (b.declinedAt?.getTime() ?? 0) - (a.declinedAt?.getTime() ?? 0));
}

// Snapshot for the share doc. Strips status — the customer's copy
// starts undecided every time an estimate is sent, so re-sending
// after an edit can't silently carry an old answer forward.
export function buildRecommendationsSnapshot(recs) {
  const clean = normalizeRecommendations(recs);
  if (clean.length === 0) return null;
  return clean.map(({ id, label, detail, price, urgency, photoUrls }) => ({
    id, label, detail, price, urgency, photoUrls,
  }));
}
